import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, User, Tag, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPost = () => {
  const { blogId } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlog();
  }, [blogId]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blog/posts/${blogId}`);
      setBlog(response.data.post);
    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto px-6 py-20 text-center">
          <p className="text-gray-600">Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto px-6 py-20 text-center">
          <p className="text-gray-600">Blog post not found</p>
          <Button className="mt-4" onClick={() => navigate('/blog')}>
            Back to Blog
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <article className="py-12">
        <div className="container mx-auto px-6 max-w-4xl">
          <Button
            variant="outline"
            className="mb-6"
            onClick={() => navigate('/blog')}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>

          {blog.image_url && (
            <div 
              className="h-96 bg-cover bg-center rounded-xl shadow-xl mb-8"
              style={{ backgroundImage: `url('${blog.image_url}')` }}
            />
          )}

          <h1 className="text-5xl font-bold text-[#0B2C4D] mb-4">{blog.title}</h1>
          
          {blog.subtitle && (
            <h2 className="text-2xl text-gray-600 mb-6">{blog.subtitle}</h2>
          )}

          <div className="flex items-center gap-6 text-gray-500 mb-8 pb-6 border-b">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5" />
              <span>{blog.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span>{new Date(blog.created_at).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</span>
            </div>
          </div>

          {blog.hashtags && blog.hashtags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {blog.hashtags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 bg-[#0B2C4D] text-white px-4 py-2 rounded-full text-sm font-semibold"
                >
                  <Tag className="w-4 h-4" />
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div 
            className="prose prose-lg max-w-none text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />

          <div className="mt-12 pt-8 border-t">
            <div className="bg-gradient-to-r from-[#0B2C4D] to-[#1a3a5a] rounded-lg p-8 text-white text-center">
              <h3 className="text-3xl font-bold mb-4">Ready to Export?</h3>
              <p className="text-xl mb-6">Contact us for professional export facilitation services</p>
              <Button
                size="lg"
                className="bg-[#C9A24D] hover:bg-[#b8923e] text-[#0B2C4D] font-semibold text-lg px-10"
                onClick={() => navigate('/contact')}
              >
                Contact Us Now
              </Button>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPost;
